import type { StylesConfig } from "react-select";
import {
  brand,
  colors,
  shadows,
  radii,
  zIndex,
} from "../../../../config/theme";

// Genérica para que cada <Select> infiera su Option del contexto: devolver
// StylesConfig a secas fijaba Option = unknown y los onChange recibían {}.
export const getCustomSelectStyles = <Option = unknown, IsMulti extends boolean = boolean>(
  isDarkMode: boolean,
  hasError: boolean = false,
): StylesConfig<Option, IsMulti> => ({
  control: (provided, state) => ({
    ...provided,
    backgroundColor: isDarkMode ? colors.dark.card : colors.light.surface,
    border: hasError
      ? `1px solid ${colors.status.error}`
      : isDarkMode
        ? `1px solid ${colors.dark.border}`
        : `1px solid ${colors.light.border}`,
    borderRadius: radii.lg,
    minHeight: "36px",
    boxShadow: "none",
    fontSize: "0.875rem",
    "&:hover": {
      borderColor: hasError
        ? colors.status.error
        : isDarkMode
          ? colors.dark.hover
          : colors.light.borderSecondary,
    },
    ...(state.isFocused && {
      borderColor: hasError ? colors.status.error : brand.primary,
      outline: "none",
      boxShadow: hasError
        ? `0 0 0 2px ${colors.status.errorLight}`
        : "0 0 0 2px rgba(116, 250, 76, 0.2)",
    }),
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 0.5rem",
    maxHeight: "100px",
    overflowY: "auto",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: isDarkMode ? colors.dark.border : colors.light.border,
    borderRadius: radii.sm,
    margin: "2px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.secondary,
    fontSize: "0.75rem",
    padding: "2px 6px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.secondary : colors.text.light.muted,
    "&:hover": {
      backgroundColor: isDarkMode
        ? colors.dark.hover
        : colors.light.borderSecondary,
      color: isDarkMode
        ? colors.text.dark.primary
        : colors.text.light.secondary,
    },
  }),
  input: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.secondary,
    margin: "0",
    padding: "0",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.secondary,
    fontSize: "0.875rem",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: colors.text.light.subtle,
    fontSize: "0.875rem",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.secondary : colors.text.light.muted,
    padding: "0 0.5rem",
    "&:hover": {
      color: isDarkMode
        ? colors.text.dark.primary
        : colors.text.light.secondary,
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: isDarkMode ? colors.dark.card : colors.light.card,
    borderRadius: radii.lg,
    marginTop: "0.25rem",
    boxShadow: shadows.dropdown,
    border: isDarkMode
      ? `1px solid ${colors.dark.border}`
      : `1px solid ${colors.light.border}`,
    zIndex: zIndex.selectMenu,
    overflow: "visible",
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0.5rem",
    maxHeight: "400px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? isDarkMode
        ? colors.dark.border
        : colors.light.border
      : state.isFocused
        ? isDarkMode
          ? colors.dark.border
          : colors.light.hover
        : "transparent",
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.secondary,
    cursor: "pointer",
    padding: "0.5rem 0.75rem",
    borderRadius: radii.md,
    fontSize: "0.75rem",
    marginBottom: "0.125rem",
    "&:last-child": {
      marginBottom: 0,
    },
    "&:active": {
      backgroundColor: isDarkMode ? colors.dark.hover : colors.light.border,
    },
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: zIndex.selectMenu,
  }),
});
